import { memo, useMemo } from 'react';
import { BriefcaseBusiness, Handshake, Tags } from 'lucide-react';
import InvestorLogo from '../../../components/data/InvestorLogo';
import CountryFlag from '../../../components/countryFlags/countryFlag';
import InvestorAccessIcons from '../../../components/investor-display/InvestorAccessIcons';
import InvestorContactDetails from '../../../components/investor-display/InvestorContactDetails';
import InvestorIndustries from '../../../components/investor-display/InvestorIndustries';
import InvestorShortInfo from '../../../components/investor-display/InvestorShortInfo';
import {
  asString,
  getInvestorIndustries,
  getInvestorTypes,
} from '../../../components/investor-display/helpers';
import { Dialog } from '../../../components/ui/dialog';

const META_PILL_CLASS =
  'inline-flex items-center gap-1 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-xs text-stone-700';

function getDealCounts(investor) {
  const leadIds = Array.isArray(investor?.as_lead_investor)
    ? investor.as_lead_investor.map(String)
    : [];
  const participatedIds = Array.isArray(investor?.as_investor_participated)
    ? investor.as_investor_participated.map(String)
    : [];
  const leadSet = new Set(leadIds);
  const participatedSet = new Set(participatedIds);

  let overlap = 0;

  for (const id of leadSet) {
    if (participatedSet.has(id)) {
      overlap += 1;
    }
  }

  return {
    leadCount: leadSet.size,
    participatedCount: participatedSet.size,
    totalDeals: leadSet.size + participatedSet.size - overlap,
  };
}

const MetaPill = memo(function MetaPill({ icon: Icon, text }) {
  if (!text) {
    return null;
  }

  return (
    <span className={META_PILL_CLASS}>
      <Icon size={14} strokeWidth={1.8} className="flex-shrink-0" />
      <span>{text}</span>
    </span>
  );
});

const DraggableItemInsights = memo(function DraggableItemInsights({
  investor,
  isOpen,
  onClose,
  t,
}) {
  const investorTypes = useMemo(() => getInvestorTypes(investor), [investor]);
  const investorIndustries = useMemo(() => getInvestorIndustries(investor), [investor]);
  const shortInfo = useMemo(() => asString(investor?.short_info).trim(), [investor?.short_info]);
  const country = useMemo(() => asString(investor?.country).trim(), [investor?.country]);
  const { leadCount, participatedCount, totalDeals } = useMemo(
    () => getDealCounts(investor),
    [investor]
  );

  if (!investor) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      buttonLabel={t('app:crm.t12')}
      size="lg"
      closeOnOverlayClick
      stickyFooter
      overlayClassName="bg-black/12 backdrop-blur-[8px]"
      contentClassName="max-h-[calc(100vh-5rem)]"
      bodyClassName="p-6 pb-4"
      footerClassName="bg-white/95 backdrop-blur-[2px]"
    >
      <div className="pr-8">
        <div className="flex items-start gap-3">
          <InvestorLogo
            id={investor?.id}
            country={investor?.country}
            className="h-10 w-10 flex-shrink-0"
            alt={`${investor?.name || 'Investor'} logo`}
          />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-semibold text-stone-900 break-words">
                {investor?.name || 'Unnamed Investor'}
              </h3>
              {country ? <CountryFlag countryName={country} /> : null}
            </div>

            <p className="mt-1 text-sm text-stone-500">{t('app:crm.t51')}</p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <MetaPill
            icon={BriefcaseBusiness}
            text={
              investorTypes.length
                ? `${t('pageFindInvestors.invType')}: ${investorTypes.join(', ')}`
                : null
            }
          />
          <MetaPill
            icon={Handshake}
            text={totalDeals > 0 ? `${t('pageFindInvestors.dealsCount')}: ${totalDeals}` : null}
          />
          <MetaPill
            icon={Tags}
            text={
              investorIndustries.length
                ? `${t('pageFindInvestors.invIndustry')}: ${investorIndustries.length}`
                : null
            }
          />
        </div>

        {shortInfo && (
          <div className="mt-5 rounded-md border border-stone-200 bg-stone-50 p-3">
            <InvestorShortInfo
              shortInfo={shortInfo}
              t={t}
              maxLength={280}
              className="text-sm leading-6 text-stone-700"
              buttonClassName="mt-2 inline-flex cursor-pointer items-center gap-1 rounded-full bg-white px-2 py-1 text-xs text-stone-700 hover:bg-stone-100"
            />
          </div>
        )}

        {(leadCount > 0 || participatedCount > 0) && (
          <div className="mt-5 rounded-md border border-stone-200 bg-stone-50 p-3">
            <div className="flex flex-wrap gap-2">
              {leadCount > 0 && (
                <span className={META_PILL_CLASS}>
                  {t('pageInvProf.labelLead')}: {leadCount}
                </span>
              )}
              {participatedCount > 0 && (
                <span className={META_PILL_CLASS}>
                  {t('pageInvProf.labelSecond')}: {participatedCount}
                </span>
              )}
            </div>
          </div>
        )}

        {investorIndustries.length > 0 && (
          <div className="mt-5 rounded-md border border-stone-200 bg-stone-50 p-3">
            <p className="mb-3 text-sm font-medium text-stone-900">
              {t('pageFindInvestors.invIndustry')}
            </p>
            <InvestorIndustries
              industries={investorIndustries}
              t={t}
              alwaysShowAll
              className="mb-0 flex flex-wrap gap-2"
            />
          </div>
        )}

        <div className="mt-5 rounded-md border border-stone-200 bg-stone-50 p-3">
          <p className="mb-3 text-sm font-medium text-stone-900">
            {t('app:crm.t48')}
          </p>

          <InvestorContactDetails
            investor={investor}
            className="mb-3 text-sm text-stone-700"
            phoneClassName="mb-2 flex items-center gap-2 text-stone-700 hover:text-stone-900"
            emailClassName="flex items-center gap-2 text-stone-700 hover:text-stone-900"
          />

          <InvestorAccessIcons
            investor={investor}
            socialMode="direct"
            showContactIcons={false}
            className="flex items-center gap-3"
          />
        </div>
      </div>
    </Dialog>
  );
});

export default DraggableItemInsights;
